import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { SEO } from "@/components/SEO";
import { Reveal } from "@/components/Reveal";
import { RichText } from "@/components/RichText";
import childSafety from "@/lib/childSafety.json";

const TABS = [
  { key: "privacy", label: "Privacy Policy", to: "/privacy-policy", title: "Privacy Policy | TruCitizen", description: "TruCitizen Privacy Policy — how CreJik Technologies Private Limited collects, uses and protects your personal data under the DPDP Act, 2023." },
  { key: "terms", label: "Terms of Usage", to: "/terms", title: "Terms of Usage | TruCitizen", description: "TruCitizen Terms of Usage — the agreement governing your use of the TruCitizen app and website by CreJik Technologies Private Limited." },
  { key: "child-safety", label: "Child Safety", to: "/child-safety", title: "Child Safety Standards – TruCitizen", description: "TruCitizen's published standards against child sexual abuse and exploitation (CSAE): prohibited conduct, in-app reporting, enforcement and reporting to authorities." },
];

const childSafetyDoc = {
  title: childSafety.title,
  meta: [childSafety.meta],
  sections: childSafety.sections.map((s) => ({ heading: s.heading, blocks: s.blocks })),
};

const parseLegal = (raw) => {
  const lines = raw.replace(/\r/g, "").split("\n");
  const doc = { title: "", meta: [], sections: [] };
  let i = 0;
  doc.title = (lines[i++] || "").trim();
  while (i < lines.length && lines[i].trim() && !/^=+$/.test(lines[i].trim())) doc.meta.push(lines[i++].trim());
  let current = null;
  let para = [];
  const flush = () => {
    if (para.length && current) current.paragraphs.push(para.join(" "));
    para = [];
  };
  for (; i < lines.length; i++) {
    const line = lines[i];
    const next = lines[i + 1] || "";
    if (/^=+$/.test(line.trim())) continue;
    if (line.trim() && /^-{3,}$/.test(next.trim())) {
      flush();
      current = { heading: line.trim(), paragraphs: [] };
      doc.sections.push(current);
      i++;
      continue;
    }
    if (!line.trim()) { flush(); continue; }
    para.push(line.trim());
  }
  flush();
  doc.sections = doc.sections.map((s) => ({ heading: s.heading, blocks: s.paragraphs.map((text) => ({ type: "p", text, plain: true })) }));
  return doc;
};

export default function Legal({ tab }) {
  const navigate = useNavigate();
  const current = TABS.find((t) => t.key === tab);
  const isChild = tab === "child-safety";
  const [doc, setDoc] = useState(null);

  useEffect(() => {
    if (isChild) { setDoc(childSafetyDoc); return; }
    setDoc(null);
    fetch(tab === "privacy" ? "/legal/privacy.txt" : "/legal/terms.txt").then((r) => r.text()).then((t) => setDoc(parseLegal(t)));
  }, [tab, isChild]);

  return (
    <section className="max-w-4xl mx-auto px-5 sm:px-8 pt-32 sm:pt-36 pb-16 sm:pb-28" data-testid="legal-page">
      <SEO title={current.title} description={current.description} path={current.to} />
      <Reveal>
        <p className="eyebrow mb-4">Legal</p>
        <div className="inline-flex flex-wrap p-1 rounded-full bg-black/[0.06]" role="tablist" data-testid="legal-tabs">
          {TABS.map(({ key, label, to }) => (
            <button key={key} role="tab" aria-selected={tab === key} onClick={() => navigate(to)} data-testid={`legal-tab-${key}`}
              className={`relative px-4 sm:px-5 py-2 rounded-full text-sm font-bold transition-colors ${tab === key ? "text-white" : "text-[var(--ink-soft)] hover:text-[var(--ink)]"}`}>
              {tab === key && <motion.span layoutId="legal-pill" className="absolute inset-0 rounded-full bg-[var(--ink)]" transition={{ type: "spring", stiffness: 400, damping: 32 }} />}
              <span className="relative z-10">{label}</span>
            </button>
          ))}
        </div>
      </Reveal>
      <AnimatePresence mode="wait">
        <motion.div key={tab} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -16 }} transition={{ duration: 0.35 }}>
          <h1 className="mt-8 font-display font-extrabold tracking-tight text-4xl sm:text-5xl text-[var(--ink)]" data-testid="legal-title">
            {isChild ? childSafety.title : current.label}
          </h1>
          {doc ? (
            <>
              <p className="mt-3 text-sm text-[var(--muted)] font-mono">{doc.meta.join(" · ")}</p>
              <div className="mt-10 space-y-6" data-testid="legal-content">
                {doc.sections.map((s) => (
                  <section key={s.heading} className="glass rounded-2xl p-6 sm:p-8">
                    <h2 className="font-display font-bold text-lg sm:text-xl text-[var(--ink)]">{s.heading}</h2>
                    {s.blocks.map((b, i) =>
                      b.type === "ul" ? (
                        <ul key={i} className="mt-3 space-y-2 list-disc pl-5 text-[var(--ink-soft)] leading-relaxed">
                          {b.items.map((item, j) => <li key={j}><RichText text={item} /></li>)}
                        </ul>
                      ) : (
                        <p key={i} className="mt-3 text-[var(--ink-soft)] leading-relaxed">{b.plain ? b.text : <RichText text={b.text} />}</p>
                      )
                    )}
                  </section>
                ))}
              </div>
            </>
          ) : (
            <div className="mt-10 space-y-6" data-testid="legal-loading">{[0, 1, 2].map((i) => <div key={i} className="h-32 rounded-2xl bg-white/50 animate-pulse" />)}</div>
          )}
        </motion.div>
      </AnimatePresence>
    </section>
  );
}
